import styled from '@emotion/styled';
import { heapTrack } from '@modernanimal/common';
import {
    StaffAppointmentAppointmentTypeEnum
} from '@modernanimal/common/src/__generated__/graphql';
import Spinner from '@modernanimal/common/src/web/AsyncButton/Spinner';
import AvailabilityCalendar, {
    AvailabilityByDateType
} from '@modernanimal/common/src/web/AvailabilityCalendar';
import {
    FilterSelections
} from '@modernanimal/common/src/web/SmartBookingFlow/smartBookingFlow.helpers';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';

import { LocationPartsFragment } from '../__generated__/graphql';

type Props = {
    addDisabledDates: (dates: string[])=> void;
    appointmentFilters: FilterSelections;
    disabledDates: string[];
    hasPreselectedAppointmentSlot: boolean;
    loading: boolean;
    selectedLocation: LocationPartsFragment | undefined;
    updateAppointmentFiltersFromCalendar: (filters: Partial<FilterSelections>)=> void;
    setAvailableDates: (dates: string[])=> void;
    setFirstAvailableSlotDate: (hasLoadedAvailability: boolean, date: FilterSelections['selectedDate'] | null)=> void;
};

const AppointmentCalendar = (props: Props) => {
    const {
        addDisabledDates,
        appointmentFilters,
        disabledDates,
        hasPreselectedAppointmentSlot,
        loading,
        selectedLocation,
        updateAppointmentFiltersFromCalendar,
        setAvailableDates,
        setFirstAvailableSlotDate,
    } = props;

    const getFirstAvailableSlot = (availabilityByDate: AvailabilityByDateType) => {
        // If no slots available on today's date, default to first available date
        const nowDateISO = DateTime.now().toISODate();
        let firstAvailableSlot = appointmentFilters.selectedDate;
        for (const [date, availability] of Object.entries(availabilityByDate)) {
            if (availability.isAvailable && date >= nowDateISO) {
                firstAvailableSlot = DateTime.fromISO(date);
                break;
            }
        }

        const unavailableDates = [];
        const availableDates = [];
        for (const isoDate of Object.keys(availabilityByDate)) {
            const dateData = availabilityByDate[isoDate];
            if (!dateData.isAvailable || isoDate < nowDateISO) {
                unavailableDates.push(isoDate);
            } else {
                availableDates.push(isoDate);
            }
        }

        let selectedDate: DateTime | null = null;
        // const isCurrentMonth = firstAvailableSlot.month === DateTime.now().month;
        if (!hasPreselectedAppointmentSlot) {
            selectedDate = firstAvailableSlot;
        }

        addDisabledDates(unavailableDates);
        setAvailableDates(availableDates);
        setFirstAvailableSlotDate(true, selectedDate);
    };

    const setFilterSelections = (updatedFilterSelections: Partial<FilterSelections>) => {
        updateAppointmentFiltersFromCalendar(updatedFilterSelections);
        heapTrack('Web Booking - Date Selected');
        posthog.capture('web_booking_click', { screen: 'AppointmentSelection', action: 'DateSelected' });
    };

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner />
            </SpinnerContainer>
        );
    }

    const threeMonthsOut = DateTime.local().plus({ months: 3 }).toJSDate();
    return (
        <AvailabilityCalendar
            appointmentType={StaffAppointmentAppointmentTypeEnum.Standard}
            disabledDates={disabledDates}
            filterSelections={appointmentFilters}
            hasStaffFilter={false}
            initialTimezone={selectedLocation?.region.timezone}
            maxDate={threeMonthsOut}
            onGetAvailability={getFirstAvailableSlot}
            setFilterSelections={setFilterSelections}
            setIsValidDateSelection={() => { }}
            showNeighboringMonth={false}
        />
    );
};

export default AppointmentCalendar;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
`;
